import { Service } from '@toeverything/infra';
import { applyUpdate } from 'yjs';

import {
  copyWorkspaceContentDB,
  transformWorkspaceDBLocalToCloud,
} from '../../db/utils';
import type { Workspace } from '../entities/workspace';
import type { WorkspaceMetadata } from '../metadata';
import type { WorkspaceDestroyService } from './destroy';
import type { WorkspaceFactoryService } from './factory';

const DUPLICATE_SOURCE_TIMEOUT = 60_000;

export class WorkspaceTransformService extends Service {
  constructor(
    private readonly factory: WorkspaceFactoryService,
    private readonly destroy: WorkspaceDestroyService
  ) {
    super();
  }

  duplicateWorkspace = async (
    source: Workspace,
    copyName: string
  ): Promise<WorkspaceMetadata> => {
    // Ensure every remote document and blob is available in local storage before
    // the factory reads it. Local workspaces use the same path, minus the remote
    // blob download.
    const sourceDocCollection = source.docCollection;
    const sourceSyncAbort = new AbortController();
    const sourceSyncTimeout = setTimeout(() => {
      sourceSyncAbort.abort(
        new Error('Timed out while preparing workspace content')
      );
    }, DUPLICATE_SOURCE_TIMEOUT);
    const removePriorities: Array<() => void> = [];

    try {
      await source.engine.doc.waitForDocReady(
        source.id,
        sourceSyncAbort.signal
      );

      // Opening an inactive workspace populates the document list only after
      // the root document is ready.
      const sourceDocIds = Array.from(sourceDocCollection.docs.keys());
      for (const docId of sourceDocIds) {
        const doc = sourceDocCollection.getDoc(docId);
        if (!doc) {
          continue;
        }
        doc.load();
        removePriorities.push(source.engine.doc.addPriority(docId, 100));
        await source.engine.doc.waitForDocReady(docId, sourceSyncAbort.signal);
      }

      await source.engine.doc.waitForSynced(undefined, sourceSyncAbort.signal);
      if (source.flavour !== 'local') {
        await source.engine.blob.fullDownload(
          undefined,
          sourceSyncAbort.signal
        );
      }

      clearTimeout(sourceSyncTimeout);
      const sourceDocStorage = source.engine.doc.storage;
      let destinationMetadata: WorkspaceMetadata | undefined;

      try {
        return await this.factory.create(
          source.flavour,
          async (docCollection, blobStorage, docStorage) => {
            // The provider has allocated the destination by this point. Keep its
            // metadata so a later failure can clean it up.
            if (docCollection.id === source.id) {
              throw new Error('Duplicate workspace must have a new id');
            }
            destinationMetadata = {
              id: docCollection.id,
              flavour: source.flavour,
            };

            const rootDocBinary = (
              await sourceDocStorage.getDoc(sourceDocCollection.doc.guid)
            )?.bin;
            if (!rootDocBinary) {
              throw new Error('Workspace root document is unavailable');
            }
            applyUpdate(docCollection.doc, rootDocBinary);
            docCollection.doc.getMap('meta').set('name', copyName);

            for (const docId of sourceDocIds) {
              const sourceDocBinary = (await sourceDocStorage.getDoc(docId))
                ?.bin;
              if (!sourceDocBinary) {
                throw new Error(`Workspace document ${docId} is unavailable`);
              }

              const doc = docCollection.getDoc(docId);
              if (!doc) {
                throw new Error(`Workspace document ${docId} was not created`);
              }
              doc.load();
              applyUpdate(doc.spaceDoc, sourceDocBinary);
            }

            await copyWorkspaceContentDB(sourceDocStorage, docStorage);

            const blobs = await source.engine.blob.storage.list();
            for (const { key } of blobs) {
              const blob = await source.engine.blob.storage.get(key);
              if (!blob) {
                throw new Error(`Workspace blob ${key} is unavailable`);
              }
              await blobStorage.set(blob);
            }
          }
        );
      } catch (error) {
        if (destinationMetadata) {
          await this.destroy.deleteWorkspace(destinationMetadata).catch(() => {
            // Cleanup is best-effort; retain the original duplication error.
          });
        }
        throw error;
      }
    } finally {
      clearTimeout(sourceSyncTimeout);
      removePriorities.forEach(removePriority => removePriority());
    }
  };

  /**
   * helper function to transform local workspace to cloud workspace
   *
   * @param accountId - all local user data will be transformed to this account
   */
  transformLocalToCloud = async (
    local: Workspace,
    accountId: string,
    flavour: string
  ): Promise<WorkspaceMetadata> => {
    if (local.flavour !== 'local') {
      throw new Error(
        'Only local workspace can be transformed to cloud workspace'
      );
    }

    const localDocStorage = local.engine.doc.storage;
    const localDocList = Array.from(local.docCollection.docs.keys());

    const newMetadata = await this.factory.create(
      flavour,
      async (docCollection, blobStorage, docStorage) => {
        const rootDocBinary = (
          await localDocStorage.getDoc(local.docCollection.doc.guid)
        )?.bin;

        if (rootDocBinary) {
          applyUpdate(docCollection.doc, rootDocBinary);
        }

        for (const subdocId of localDocList) {
          const subdocBinary = (await localDocStorage.getDoc(subdocId))?.bin;
          if (subdocBinary) {
            const doc = docCollection.getDoc(subdocId);
            if (doc) {
              const spaceDoc = doc.spaceDoc;
              doc.load();
              applyUpdate(spaceDoc, subdocBinary);
            }
          }
        }

        // transform db
        await transformWorkspaceDBLocalToCloud(
          local.id,
          docCollection.id,
          localDocStorage,
          docStorage,
          accountId
        );

        const blobList = await local.engine.blob.storage.list();

        for (const { key } of blobList) {
          const blob = await local.engine.blob.storage.get(key);
          if (blob) {
            await blobStorage.set(blob);
          }
        }
      }
    );

    await this.destroy.deleteWorkspace(local.meta);

    return newMetadata;
  };
}
